import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboard, cuentas } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Users,
  DollarSign,
  Calendar,
  TrendingUp
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function ClientesPagos() {
  const [loading, setLoading] = useState(true);
  const [cuentasList, setCuentasList] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('consolidado');
  const [clientesData, setClientesData] = useState([]);

  useEffect(() => {
    loadCuentas();
  }, []);

  useEffect(() => {
    loadClientesData();
  }, [selectedCuenta]);

  const loadCuentas = async () => {
    try {
      const response = await cuentas.getAll();
      setCuentasList(response.data);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadClientesData = async () => {
    setLoading(true);
    try {
      const params = selectedCuenta === 'consolidado' 
        ? { consolidado: true } 
        : { cuenta_id: selectedCuenta };

      const response = await dashboard.getClientesPagos(params);
      setClientesData(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalRecibido = clientesData.reduce((sum, c) => sum + c.total_pagado, 0);
  const totalClientes = clientesData.length;
  const promedioCliente = totalClientes > 0 ? totalRecibido / totalClientes : 0;

  // Chart data - top 10 clients
  const chartData = clientesData.slice(0, 10).map(c => ({
    name: c.cliente?.length > 15 ? c.cliente.substring(0, 15) + '...' : c.cliente,
    total: c.total_pagado
  }));

  if (loading) {
    return (
      <div className="p-8">
        <div className="kpi-grid mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl"></div>
          ))}
        </div>
        <div className="skeleton h-80 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div data-testid="clientes-pagos-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Control de Clientes
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitorea los pagos recibidos de tus clientes
            </p>
          </div>
          <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
            <SelectTrigger className="w-48" data-testid="cuenta-selector">
              <SelectValue placeholder="Seleccionar cuenta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consolidado">Consolidado</SelectItem>
              {cuentasList.map((cuenta) => (
                <SelectItem key={cuenta.id} value={cuenta.id}>
                  {cuenta.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Recibido</p>
                  <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalRecibido)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-100">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Activos</p>
                  <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {totalClientes}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-primary/10">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Promedio por Cliente</p>
                  <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(promedioCliente)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <TrendingUp className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <Card className="chart-container mb-6">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Top 10 Clientes por Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#64748B" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#64748B" width={120} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Bar 
                      dataKey="total" 
                      fill="#BE123C" 
                      radius={[0, 4, 4, 0]}
                      name="Total Pagado"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clients Table */}
        <Card className="table-container">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Listado de Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="clientes-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th className="text-right">Total Pagado</th>
                    <th className="text-center">Pagos</th>
                    <th>Último Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-12">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No hay datos de clientes</p>
                      </td>
                    </tr>
                  ) : (
                    clientesData.map((cliente, index) => (
                      <tr key={index}>
                        <td className="font-medium">{cliente.cliente}</td>
                        <td className="text-right text-emerald-600 font-bold">
                          {formatCurrency(cliente.total_pagado)}
                        </td>
                        <td className="text-center">
                          <Badge variant="secondary">{cliente.cantidad_pagos}</Badge>
                        </td>
                        <td className="text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(cliente.ultimo_pago)}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
